import { useEffect, useState } from "react";
import { emailBuilderService } from "@/lib/api-services/email-builder-service";
import { campaignService } from "@/lib/api-services/campaign-service";
import { Button } from "@/components/ui/button";
import { EmailCampaignType } from "@/types/email";
import type { WizardData } from "@/pages/campaigns/CampaignWizard";

const CAMPAIGN_TYPES = [
  { value: EmailCampaignType.Promotional, label: "Promotional", description: "Newsletters, offers, announcements" },
  { value: EmailCampaignType.Transactional, label: "Transactional", description: "Receipts, confirmations, notifications" },
  { value: EmailCampaignType.Newsletter, label: "Newsletter", description: "Regular updates for subscribers" },
];

interface StepCampaignDetailsProps {
  data: WizardData;
  preselectedDocumentId: string | null;
  onChange: (partial: Partial<WizardData>) => void;
  onBack: () => void;
  onNext: () => void;
}

export function StepCampaignDetails({ data, preselectedDocumentId, onChange, onBack, onNext }: StepCampaignDetailsProps) {
  const [docName, setDocName] = useState<string | null>(null);

  useEffect(() => {
    if (preselectedDocumentId && !data.selectedDocument) {
      emailBuilderService.getDocument(preselectedDocumentId).then((doc) => {
        setDocName(doc.name);
        if (!data.name) onChange({ name: doc.name, selectedDocument: doc });
      }).catch(() => {});
    }
  }, [preselectedDocumentId]);

  // Campaigns can only be sent from the verified sender address, so it is fixed, not typed
  const [senderError, setSenderError] = useState<string | null>(null);
  useEffect(() => {
    campaignService.getSender()
      .then(({ senderAddress }) => {
        if (senderAddress && data.fromEmail !== senderAddress) onChange({ fromEmail: senderAddress });
      })
      .catch(() => setSenderError("Could not load the sender address."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canProceed =
    data.name.trim() &&
    data.subject.trim() &&
    data.fromName.trim() &&
    data.fromEmail.trim();

  const handleNext = () => {
    if (canProceed) onNext();
  };

  return (
    <div className="lw-step-campaign-details">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground">Campaign details</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Name your campaign and configure how it appears in inboxes.
        </p>
      </div>

      {/* Selected document indicator */}
      {(data.selectedDocument || (preselectedDocumentId && docName)) && (
        <div className="mb-5 rounded-md bg-primary/5 border border-primary/20 px-3 py-2 flex items-center gap-2">
          <svg className="h-4 w-4 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="text-xs text-foreground font-medium">
            Email: {data.selectedDocument?.name ?? docName}
          </span>
        </div>
      )}

      <div className="space-y-5">
        {/* Campaign name */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Campaign name <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="e.g. Summer Sale 2025"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {/* Campaign type */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Campaign type <span className="text-destructive">*</span>
          </label>
          <div className="grid grid-cols-3 gap-2">
            {CAMPAIGN_TYPES.map((t) => (
              <button
                key={t.value}
                onClick={() => onChange({ type: t.value })}
                className={`rounded-lg border p-3 text-left transition-all ${
                  data.type === t.value
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border bg-card hover:border-primary/40"
                }`}
              >
                <p className="text-xs font-semibold text-foreground">{t.label}</p>
                <p className="mt-0.5 text-[10px] text-muted-foreground">{t.description}</p>
              </button>
            ))}
          </div>
        </div>

        <hr className="border-border" />

        {/* Subject */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Subject line <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <input
              type="text"
              value={data.subject}
              onChange={(e) => onChange({ subject: e.target.value.slice(0, 500) })}
              placeholder="e.g. Don't miss our biggest sale of the year 🎉"
              maxLength={500}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring pr-12"
            />
            <span className="absolute right-3 top-2 text-xs text-muted-foreground">
              {data.subject.length}/500
            </span>
          </div>
        </div>

        {/* Preview text */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Preview text
            <span className="ml-1 text-xs text-muted-foreground font-normal">(shown after subject in inbox)</span>
          </label>
          <input
            type="text"
            value={data.previewText}
            onChange={(e) => onChange({ previewText: e.target.value.slice(0, 500) })}
            placeholder="e.g. Up to 50% off everything — today only"
            maxLength={500}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <hr className="border-border" />

        {/* From name */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            From name <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            value={data.fromName}
            onChange={(e) => onChange({ fromName: e.target.value })}
            placeholder="e.g. Acme Inc."
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {/* From email */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            From email <span className="text-destructive">*</span>
          </label>
          <input
            type="email"
            value={data.fromEmail}
            readOnly
            placeholder="Loading sender address…"
            className="w-full rounded-md border border-input bg-muted px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          <p className={`mt-1 text-xs ${senderError ? "text-destructive" : "text-muted-foreground"}`}>
            {senderError ?? "Emails are sent from your verified sender address. Use Reply-to to receive replies elsewhere."}
          </p>
        </div>

        {/* Reply-to */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Reply-to email
            <span className="ml-1 text-xs text-muted-foreground font-normal">(optional)</span>
          </label>
          <input
            type="email"
            value={data.replyToEmail}
            onChange={(e) => onChange({ replyToEmail: e.target.value })}
            placeholder="replies@example.com"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      {/* Navigation */}
      <div className="mt-8 flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <Button onClick={handleNext} disabled={!canProceed}>
          Next: Recipients
        </Button>
      </div>
    </div>
  );
}
